import { migrationPageIndicator } from '3-foundation/PageContent/connector';
import { getNavigationFromCDA } from './connector';
import logger from '3-foundation/Logging/logger';
import { createMemoryCache } from '3-foundation/Cache/memoryCache';
import { NAVIGATION_CACHE_TTL } from '3-foundation/_constants';

export interface GetNavigationOptions {
	byName?: string;
	byId?: string;
	currentUrl?: string;
	includeHidden?: boolean;
	includeRootItem?: boolean;
}
export interface NavigationRepositoryInterface {
	getNavigation: (options?: GetNavigationOptions) => Navigation.NavigationItem[];
	getBreadcrumb: (options?: GetNavigationOptions) => Navigation.NavigationItem[];
	getLocalNavigation: (options?: GetNavigationOptions) => Navigation.NavigationItem;
}

export const NavigationRepositoryFactory = (data: Navigation.NavigationItem): NavigationRepositoryInterface => {
	if (!data) return;

	const createNavigationItem = (navigation: Navigation.NavigationItem, options?: GetNavigationOptions): Navigation.NavigationItem => {
		if (navigation.hidden && !options?.includeHidden) {
			return null;
		}

		const descendants = navigation.descendants
			?.map((item) => {
				return createNavigationItem(item, options);
			})
			?.filter((item) => item);
		const isCurrent = options?.currentUrl ? options?.currentUrl === navigation.url : false;
		const isActive = isCurrent || descendants?.find((item) => item.current || item.active) ? true : false;

		return {
			id: navigation.id,
			contentType: navigation.contentType,
			name: navigation.name,
			url: navigation.url,
			descendants: descendants?.length === 0 ? null : descendants,
			title: navigation.title ?? null,
			hidden: navigation.hidden ?? null,
			current: isCurrent,
			active: isActive,
		};
	};

	const getNavigationItems = (options?: GetNavigationOptions): Navigation.NavigationItem[] => {
		let root = data;

		if (options?.byName) {
			root = findByName(options.byName, root);
		} else if (options?.byId) {
			root = findById(options.byId, root);
		}

		if (!root) return null;

		const itemsToReturn = options.includeRootItem ? [root] : root.descendants;

		if (!itemsToReturn) {
			logger.error(`Reference error - NavigationRepository: Navigation items array is undefined for ${root?.name}`);
			return []; // If there are no items to return, return an empty list.
		}

		return itemsToReturn.map((item) => createNavigationItem(item, options))?.filter((item) => item);
	};

	return {
		// Return visible navigation items.
		// This is only done for the first level for now
		// and could be refined if the need arises.
		getNavigation(options): Navigation.NavigationItem[] {
			return getNavigationItems(options);
		},

		// Returns the bread crumb
		// For now just the root and the current item (if any)
		getBreadcrumb(options): Navigation.NavigationItem[] {
			const flattenItems = (items: Navigation.NavigationItem[]) => {
				const flattened = [];
				items.forEach((element) => {
					flattened.push(element);
					if (element.descendants) {
						const flattenedDesc = flattenItems(element.descendants);
						flattenedDesc.forEach((f) => flattened.push(f));
					}
				});
				return flattened;
			};
			// Set includeHidden to true to ensure hidden items are included in breadcrumb
			const items = getNavigationItems({ ...options, includeHidden: true });
			return flattenItems(items).filter((item) => item.active);
		},

		// Returns the local navigation for the current context
		getLocalNavigation(options): Navigation.NavigationItem | null {
			const { byId, currentUrl } = options || {};
			const items = getNavigationItems({ includeRootItem: false, includeHidden: true }) || [];
			// Find path to item by ID and set current
			const findPathById = (
				nodes: Navigation.NavigationItem[],
				targetId: string,
				path: Navigation.NavigationItem[] = [],
			): Navigation.NavigationItem[] | null => {
				for (const node of nodes) {
					const markedNode: Navigation.NavigationItem = {
						...node,
						current: node.url === currentUrl,
						descendants: node.descendants?.map((child) => ({
							...child,
							current: child.url === currentUrl,
						})),
					};
					const newPath = [...path, markedNode];
					if (node.id === targetId) {
						return newPath;
					}
					if (node.descendants) {
						const result = findPathById(node.descendants, targetId, newPath);
						if (result) {
							return result;
						}
					}
				}
				return null;
			};

			const path = findPathById(items, byId);
			if (!path) return null;
			const trimmedTypeLocal = (contentType) => contentType?.replace(migrationPageIndicator, '');

			// Find the appropriate contentPage as navigation root
			// If the first item in path is a contentPage, use it
			// Otherwise, find the first contentPage in the path (which will be in the correct branch)
			let navigationRoot = null;
			const rootNode = path[0];
			if (trimmedTypeLocal(rootNode.contentType) === 'contentPage') {
				navigationRoot = rootNode;
			} else {
				// Find the first contentPage in the path
				navigationRoot = path.find((n) => trimmedTypeLocal(n.contentType) === 'contentPage');
			}
			if (!navigationRoot) return null;

			// Recursively mark current and active nodes in the subtree, skipping hidden and hideInNavigation ONLY for local navigation
			const markCurrentAndActiveLocal = (node, isRoot = false) => {
				if (!node) return null;
				// Only filter hidden items if they are not the root
				if (!isRoot && (node.hidden || node.hideInNavigation)) return null;
				const isCurrent = node.url === currentUrl;
				const descendants = node.descendants ? node.descendants.map((d) => markCurrentAndActiveLocal(d, false)).filter(Boolean) : [];
				const isActive = isCurrent || descendants.some((d) => d && (d.current || d.active));
				return {
					...node,
					current: isCurrent,
					active: isActive,
					descendants,
				};
			};

			return markCurrentAndActiveLocal(navigationRoot, true);
		},
	};
};

export interface GetNavigationProps {
	siteId: string;
	locale: string;
	root: Site.Site['root'];
	preview?: boolean;
}

const navigationCache = createMemoryCache<Navigation.NavigationItem>('navigation', NAVIGATION_CACHE_TTL);

export const getNavigation = async ({ siteId, locale, root, preview }: GetNavigationProps): Promise<Navigation.NavigationItem> => {
	const load = () => getNavigationFromCDA({ root, levels: 6, preview, locale, siteId });
	// Preview shows draft content, so it is never cached
	if (preview) return await load();
	return await navigationCache({ siteId, key: locale }, load);
};

// Return navigation items by its name.
// This is only done for the first level of descendants for now
// and could be refined if the need arises.
const findByName = (name: string, item: Navigation.NavigationItem): Navigation.NavigationItem => {
	const normalizedName = name.toLowerCase();
	if (item.name === normalizedName) {
		return item;
	} else {
		const foundItem = item.descendants.filter((item) => {
			return item.name.toLowerCase() === normalizedName;
		});
		if (foundItem.length <= 0) {
			logger.error('Reference error - NavigationRepository: FindByName returned not found name:', name);
		}
		return foundItem.length ? foundItem[0] : { id: '0', name: '', url: '' };
	}
};
// Return navigation item by its ID.
// Searches in all levels of the navigation tree using a breadth-first search approach.
const findById = (id: string, item: Navigation.NavigationItem) => {
	if (item.id === id) {
		return item;
	}

	const items = [item];

	while (items.length > 0) {
		const currentItem = items.shift();

		if (currentItem?.id === id) {
			return currentItem;
		}

		if (currentItem?.descendants) {
			items.push(...currentItem.descendants);
		}
	}

	return null;
};
